<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Button Component
      p.text--secondary A versatile button component with multiple variations and states.

    // Button Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in buttonVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped buttons (side by side)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="button in variation.buttons" :key="button.label" class="d-flex flex-column align-center")
                  yz-btn(v-if="button.props.iconOnly || button.props.icon" v-bind="button.props")
                  yz-btn(v-else :label="button.label" v-bind="button.props")
                  small.text--secondary.mt-2(v-if="button.hint") {{ button.hint }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="button in variation.buttons" :key="button.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === button.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === button.code ? 'success' : ''" @click="copyCode(button.code)")
                    span.font-weight-medium.ml-2 {{ button.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ button.code }}

          // Individual buttons
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              yz-btn(:label="variation.label" v-bind="variation.props")

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in buttonProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      buttonVariations: [
        {
          name: 'Basic Buttons',
          id: 'basic-buttons',
          description: 'Primary and other variations of button styles',
          isGroup: true,
          buttons: [
            {
              label: 'Primary',
              props: {},
              code: 'yz-btn(label="Primary")',
            },
            {
              label: 'Secondary',
              props: {
                secondary: true
              },
              code: 'yz-btn(label="Secondary" secondary)'
            },
            {
              label: 'Outlined',
              props: {
                outlined: true
              },
              code: 'yz-btn(label="Outlined" outlined)'
            },
            {
              label: 'Text',
              props: {
                text: true
              },
              code: 'yz-btn(label="Text" text)'
            },
            {
              label: 'Underline',
              props: {
                text: true,
                underline: true
              },
              code: 'yz-btn(label="Underline" underline)'
            }
          ]
        },
        {
          name: 'Button Sizes',
          id: 'button-sizes',
          description: 'Different button sizes for various use cases',
          isGroup: true,
          buttons: [
            {
              label: 'Small',
              props: {
                size: 'sm'
              },
              code: 'yz-btn(label="Small" size="sm")'
            },
            {
              label: 'Medium',
              props: {
                size: 'md'
              },
              code: 'yz-btn(label="Medium" size="md")'
            },
            {
              label: 'Large',
              props: {
                size: 'lg'
              },
              code: 'yz-btn(label="Large" size="lg")'
            },
            {
              label: 'Extra Large',
              props: {
                size: 'xl'
              },
              code: 'yz-btn(label="Extra Large" size="xl")'
            }
          ]
        },
        {
          name: 'Loading Buttons',
          id: 'loading-buttons',
          description: 'Loading states across different sizes and variants',
          isGroup: true,
          buttons: [
            // Different variants with medium size
            {
              label: 'Primary',
              props: {
                loading: true
              },
              code: 'yz-btn(label="Primary" loading)',
              hint: 'Primary variant'
            },
            {
              label: 'Secondary',
              props: {
                secondary: true,
                loading: true
              },
              code: 'yz-btn(label="Secondary" secondary loading)',
              hint: 'Secondary variant'
            },
            {
              label: 'Outlined',
              props: {
                outlined: true,
                loading: true
              },
              code: 'yz-btn(label="Outlined" outlined loading)',
              hint: 'Outlined variant'
            },
            {
              label: 'Text',
              props: {
                text: true,
                loading: true
              },
              code: 'yz-btn(label="Text" text loading)',
              hint: 'Text variant'
            },
            {
              label: 'Underline',
              props: {
                text: true,
                underline: true,
                loading: true
              },
              code: 'yz-btn(label="Underline" underline loading)',
              hint: 'Underline variant'
            },
            // Different sizes with primary variant
            {
              label: 'Small',
              props: {
                size: 'sm',
                loading: true
              },
              code: 'yz-btn(label="Small" size="sm" loading)',
              hint: 'Small size'
            },
            {
              label: 'Large',
              props: {
                size: 'lg',
                loading: true
              },
              code: 'yz-btn(label="Large" size="lg" loading)',
              hint: 'Large size'
            },
            {
              label: 'Extra Large',
              props: {
                size: 'xl',
                loading: true
              },
              code: 'yz-btn(label="Extra Large" size="xl" loading)',
              hint: 'XL size'
            },
            // Danger color variants with loading
            {
              label: 'Danger Primary',
              props: {
                color: 'danger',
                loading: true
              },
              code: 'yz-btn(label="Danger Primary" color="danger" loading)',
              hint: 'Danger primary variant'
            },
            {
              label: 'Danger Secondary',
              props: {
                color: 'danger',
                secondary: true,
                loading: true
              },
              code: 'yz-btn(label="Danger Secondary" color="danger" secondary loading)',
              hint: 'Danger secondary variant'
            },
            {
              label: 'Danger Outlined',
              props: {
                color: 'danger',
                outlined: true,
                loading: true
              },
              code: 'yz-btn(label="Danger Outlined" color="danger" outlined loading)',
              hint: 'Danger outlined variant'
            },
            {
              label: 'Danger Text',
              props: {
                color: 'danger',
                text: true,
                loading: true
              },
              code: 'yz-btn(label="Danger Text" color="danger" text loading)',
              hint: 'Danger text variant'
            },
            {
              label: 'Danger Underline',
              props: {
                color: 'danger',
                text: true,
                underline: true,
                loading: true
              },
              code: 'yz-btn(label="Danger Underline" color="danger" text underline loading)',
              hint: 'Danger underline variant'
            }
          ]
        },
        {
          name: 'Disabled Buttons',
          id: 'disabled-buttons',
          description: 'Disabled states across different sizes and variants',
          isGroup: true,
          buttons: [
            // Different variants with medium size
            {
              label: 'Primary',
              props: {
                disabled: true
              },
              code: 'yz-btn(label="Primary" disabled)',
              hint: 'Primary variant'
            },
            {
              label: 'Secondary',
              props: {
                secondary: true,
                disabled: true
              },
              code: 'yz-btn(label="Secondary" secondary disabled)',
              hint: 'Secondary variant'
            },
            {
              label: 'Outlined',
              props: {
                outlined: true,
                disabled: true
              },
              code: 'yz-btn(label="Outlined" outlined disabled)',
              hint: 'Outlined variant'
            },
            {
              label: 'Text',
              props: {
                text: true,
                disabled: true
              },
              code: 'yz-btn(label="Text" text disabled)',
              hint: 'Text variant'
            },
            {
              label: 'Underline',
              props: {
                text: true,
                underline: true,
                disabled: true
              },
              code: 'yz-btn(label="Underline" underline disabled)',
              hint: 'Underline variant'
            },
            // Different sizes with primary variant
            {
              label: 'Small',
              props: {
                size: 'sm',
                disabled: true
              },
              code: 'yz-btn(label="Small" size="sm" disabled)',
              hint: 'Small size'
            },
            {
              label: 'Large',
              props: {
                size: 'lg',
                disabled: true
              },
              code: 'yz-btn(label="Large" size="lg" disabled)',
              hint: 'Large size'
            },
            {
              label: 'Extra Large',
              props: {
                size: 'xl',
                disabled: true
              },
              code: 'yz-btn(label="Extra Large" size="xl" disabled)',
              hint: 'XL size'
            },
            // Danger color variants with disabled
            {
              label: 'Danger Primary',
              props: {
                color: 'danger',
                disabled: true
              },
              code: 'yz-btn(label="Danger Primary" color="danger" disabled)',
              hint: 'Danger primary variant'
            },
            {
              label: 'Danger Secondary',
              props: {
                color: 'danger',
                secondary: true,
                disabled: true
              },
              code: 'yz-btn(label="Danger Secondary" color="danger" secondary disabled)',
              hint: 'Danger secondary variant'
            },
            {
              label: 'Danger Outlined',
              props: {
                color: 'danger',
                outlined: true,
                disabled: true
              },
              code: 'yz-btn(label="Danger Outlined" color="danger" outlined disabled)',
              hint: 'Danger outlined variant'
            },
            {
              label: 'Danger Text',
              props: {
                color: 'danger',
                text: true,
                disabled: true
              },
              code: 'yz-btn(label="Danger Text" color="danger" text disabled)',
              hint: 'Danger text variant'
            },
            {
              label: 'Danger Underline',
              props: {
                color: 'danger',
                text: true,
                underline: true,
                disabled: true
              },
              code: 'yz-btn(label="Danger Underline" color="danger" text underline disabled)',
              hint: 'Danger underline variant'
            }
          ]
        },
        {
          name: 'Buttons with icon',
          id: 'buttons-with-icon',
          description: 'Buttons with icon in different positions, sizes, and variants',
          isGroup: true,
          buttons: [
            // Icon positions
            {
              label: 'Prepend Icon',
              props: {
                prependIcon: 'mdi-heart'
              },
              code: 'yz-btn(label="Prepend Icon" prepend-icon="mdi-heart")',
              hint: 'Icon before text'
            },
            {
              label: 'Append Icon',
              props: {
                appendIcon: 'mdi-arrow-right'
              },
              code: 'yz-btn(label="Append Icon" append-icon="mdi-arrow-right")',
              hint: 'Icon after text'
            },
            {
              label: 'Both Icons',
              props: {
                prependIcon: 'mdi-heart',
                appendIcon: 'mdi-arrow-right'
              },
              code: 'yz-btn(label="Both Icons" prepend-icon="mdi-heart" append-icon="mdi-arrow-right")',
              hint: 'Icons on both sides'
            },
            // Different variants with prepend icon
            {
              label: 'Secondary',
              props: {
                secondary: true,
                prependIcon: 'mdi-cog'
              },
              code: 'yz-btn(label="Secondary" secondary prepend-icon="mdi-cog")',
              hint: 'Secondary variant'
            },
            {
              label: 'Outlined',
              props: {
                outlined: true,
                prependIcon: 'mdi-download'
              },
              code: 'yz-btn(label="Outlined" outlined prepend-icon="mdi-download")',
              hint: 'Outlined variant'
            },
            {
              label: 'Text',
              props: {
                text: true,
                prependIcon: 'mdi-pencil'
              },
              code: 'yz-btn(label="Text" text prepend-icon="mdi-pencil")',
              hint: 'Text variant'
            },
            // Different sizes with prepend icon
            {
              label: 'Small',
              props: {
                size: 'sm',
                prependIcon: 'mdi-plus'
              },
              code: 'yz-btn(label="Small" size="sm" prepend-icon="mdi-plus")',
              hint: 'Small size'
            },
            {
              label: 'Large',
              props: {
                size: 'lg',
                prependIcon: 'mdi-star'
              },
              code: 'yz-btn(label="Large" size="lg" prepend-icon="mdi-star")',
              hint: 'Large size'
            },
            {
              label: 'Extra Large',
              props: {
                size: 'xl',
                prependIcon: 'mdi-rocket'
              },
              code: 'yz-btn(label="Extra Large" size="xl" prepend-icon="mdi-rocket")',
              hint: 'XL size'
            },
            // Loading state with icons
            {
              label: 'Loading',
              props: {
                loading: true,
                prependIcon: 'mdi-cloud-upload'
              },
              code: 'yz-btn(label="Loading" loading prepend-icon="mdi-cloud-upload")',
              hint: 'Loading state'
            },
            {
              label: 'Loading Append',
              props: {
                loading: true,
                appendIcon: 'mdi-send'
              },
              code: 'yz-btn(label="Loading Append" loading append-icon="mdi-send")',
              hint: 'Loading with append icon'
            },
            // Loading state with danger color
            {
              label: 'Danger Loading',
              props: {
                color: 'danger',
                loading: true,
                prependIcon: 'mdi-delete'
              },
              code: 'yz-btn(label="Danger Loading" color="danger" loading prepend-icon="mdi-delete")',
              hint: 'Danger loading state'
            },
            // Disabled state with icons
            {
              label: 'Disabled',
              props: {
                disabled: true,
                prependIcon: 'mdi-lock'
              },
              code: 'yz-btn(label="Disabled" disabled prepend-icon="mdi-lock")',
              hint: 'Disabled state'
            },
            {
              label: 'Disabled Append',
              props: {
                disabled: true,
                appendIcon: 'mdi-block-helper'
              },
              code: 'yz-btn(label="Disabled Append" disabled append-icon="mdi-block-helper")',
              hint: 'Disabled with append icon'
            },
            // Danger color variants with icons
            {
              label: 'Danger Filled',
              props: {
                color: 'danger',
                prependIcon: 'mdi-delete'
              },
              code: 'yz-btn(label="Danger Filled" color="danger" prepend-icon="mdi-delete")',
              hint: 'Danger filled variant'
            },
            {
              label: 'Danger Secondary',
              props: {
                color: 'danger',
                secondary: true,
                prependIcon: 'mdi-close'
              },
              code: 'yz-btn(label="Danger Secondary" color="danger" secondary prepend-icon="mdi-close")',
              hint: 'Danger secondary variant'
            },
            {
              label: 'Danger Outlined',
              props: {
                color: 'danger',
                outlined: true,
                prependIcon: 'mdi-alert'
              },
              code: 'yz-btn(label="Danger Outlined" color="danger" outlined prepend-icon="mdi-alert")',
              hint: 'Danger outlined variant'
            },
            {
              label: 'Danger Text',
              props: {
                color: 'danger',
                text: true,
                prependIcon: 'mdi-alert'
              },
              code: 'yz-btn(label="Danger Text" color="danger" text prepend-icon="mdi-alert")',
              hint: 'Danger text variant'
            },
            {
              label: 'Danger Underline',
              props: {
                color: 'danger',
                text: true,
                underline: true,
                prependIcon: 'mdi-minus-circle'
              },
              code: 'yz-btn(label="Danger Underline" color="danger" text underline prepend-icon="mdi-minus-circle")',
              hint: 'Danger underline variant'
            },
            {
              label: 'Danger Append',
              props: {
                color: 'danger',
                appendIcon: 'mdi-trash-can'
              },
              code: 'yz-btn(label="Danger Append" color="danger" append-icon="mdi-trash-can")',
              hint: 'Danger with append icon'
            },
            {
              label: 'Danger Both Icons',
              props: {
                color: 'danger',
                prependIcon: 'mdi-alert',
                appendIcon: 'mdi-delete'
              },
              code: 'yz-btn(label="Danger Both Icons" color="danger" prepend-icon="mdi-alert" append-icon="mdi-delete")',
              hint: 'Danger with both icons'
            }
          ]
        },
        {
          name: 'Icon Buttons',
          id: 'icon-buttons',
          description: 'Icon-only buttons using icon prop with different configurations',
          isGroup: true,
          buttons: [
            // Different icons
            {
              label: 'Heart Icon',
              props: {
                iconOnly: true,
                icon: 'mdi-heart'
              },
              code: 'yz-btn(icon-only icon="mdi-heart")',
              hint: 'Icon only'
            },
            {
              label: 'Arrow Icon',
              props: {
                iconOnly: true,
                icon: 'mdi-arrow-right'
              },
              code: 'yz-btn(icon-only icon="mdi-arrow-right")',
              hint: 'Icon only'
            },
            // Different sizes
            {
              label: 'Small',
              props: {
                iconOnly: true,
                size: 'sm',
                icon: 'mdi-plus'
              },
              code: 'yz-btn(icon-only size="sm" icon="mdi-plus")',
              hint: 'Small size'
            },
            {
              label: 'Medium',
              props: {
                iconOnly: true,
                size: 'md',
                icon: 'mdi-home'
              },
              code: 'yz-btn(icon-only size="md" icon="mdi-home")',
              hint: 'Medium size'
            },
            {
              label: 'Large',
              props: {
                iconOnly: true,
                size: 'lg',
                icon: 'mdi-star'
              },
              code: 'yz-btn(icon-only size="lg" icon="mdi-star")',
              hint: 'Large size'
            },
            {
              label: 'Extra Large',
              props: {
                iconOnly: true,
                size: 'xl',
                icon: 'mdi-rocket'
              },
              code: 'yz-btn(icon-only size="xl" icon="mdi-rocket")',
              hint: 'XL size'
            },
            // Loading states in different sizes
            {
              label: 'Loading Small',
              props: {
                iconOnly: true,
                size: 'sm',
                loading: true,
                icon: 'mdi-refresh',
              },
              code: 'yz-btn(icon-only size="sm" loading icon="mdi-refresh")',
              hint: 'Small loading'
            },
            {
              label: 'Loading Medium',
              props: {
                iconOnly: true,
                size: 'md',
                loading: true,
                icon: 'mdi-refresh'
              },
              code: 'yz-btn(icon-only size="md" loading icon="mdi-refresh")',
              hint: 'Medium loading'
            },
            {
              label: 'Loading Large',
              props: {
                iconOnly: true,
                size: 'lg',
                loading: true,
                icon: 'mdi-refresh'
              },
              code: 'yz-btn(icon-only size="lg" loading icon="mdi-refresh")',
              hint: 'Large loading'
            },
            {
              label: 'Loading XL',
              props: {
                iconOnly: true,
                size: 'xl',
                loading: true,
                icon: 'mdi-refresh'
              },
              code: 'yz-btn(icon-only size="xl" loading icon="mdi-refresh")',
              hint: 'Extra large loading'
            },
            {
              label: 'Disabled',
              props: {
                iconOnly: true,
                disabled: true,
                icon: 'mdi-lock'
              },
              code: 'yz-btn(icon-only disabled icon="mdi-lock")',
              hint: 'Disabled state'
            }
          ]
        },
        {
          name: 'Outlined Buttons with Icons & Sizes',
          id: 'outlined-buttons-icons-sizes',
          description: 'Outlined buttons with icons in different sizes using $yzi-icons',
          isGroup: true,
          buttons: [
            // Small outlined buttons with icons
            {
              props: {
                outlined: true,
                size: 'sm',
                icon: '$yzi-plus',
              },
              code: 'yz-btn(outlined size="sm" icon="$yzi-plus")',
              hint: 'Small outlined with icon'
            },
            {
              props: {
                outlined: true,
                size: 'sm',
                icon: '$yzi-download-01'
              },
              code: 'yz-btn(outlined size="sm" icon="$yzi-download-01")',
              hint: 'Small outlined with icon'
            },
            {
              props: {
                outlined: true,
                size: 'sm',
                icon: '$yzi-star-01'
              },
              code: 'yz-btn(outlined size="sm" icon="$yzi-star-01")',
              hint: 'Small outlined with icon'
            },
            // Medium outlined buttons with icons
            {
              props: {
                outlined: true,
                size: 'md',
                icon: '$yzi-edit-02'
              },
              code: 'yz-btn(outlined size="md" icon="$yzi-edit-02")',
              hint: 'Medium outlined with icon'
            },
            {
              props: {
                outlined: true,
                size: 'md',
                icon: '$yzi-share-03'
              },
              code: 'yz-btn(outlined size="md" icon="$yzi-share-03")',
              hint: 'Medium outlined with icon'
            },
            {
              props: {
                outlined: true,
                size: 'md',
                icon: '$yzi-settings-02'
              },
              code: 'yz-btn(outlined size="md" icon="$yzi-settings-02")',
              hint: 'Medium outlined with icon'
            },
            // Large outlined buttons with icons
            {
              props: {
                outlined: true,
                size: 'lg',
                icon: '$yzi-upload-01'
              },
              code: 'yz-btn(outlined size="lg" icon="$yzi-upload-01")',
              hint: 'Large outlined with icon'
            },
            {
              props: {
                outlined: true,
                size: 'lg',
                icon: '$yzi-calendar'
              },
              code: 'yz-btn(outlined size="lg" icon="$yzi-calendar")',
              hint: 'Large outlined with icon'
            },
            {
              props: {
                outlined: true,
                size: 'lg',
                icon: '$yzi-users-02'
              },
              code: 'yz-btn(outlined size="lg" icon="$yzi-users-02")',
              hint: 'Large outlined with icon'
            },
            // Extra Large outlined buttons with icons
            {
              props: {
                outlined: true,
                size: 'xl',
                icon: '$yzi-file-04'
              },
              code: 'yz-btn(outlined size="xl" icon="$yzi-file-04")',
              hint: 'Extra large outlined with icon'
            },
            {
              props: {
                outlined: true,
                size: 'xl',
                icon: '$yzi-save-01'
              },
              code: 'yz-btn(outlined size="xl" icon="$yzi-save-01")',
              hint: 'Extra large outlined with icon'
            },
            {
              props: {
                outlined: true,
                size: 'xl',
                icon: '$yzi-check-circle'
              },
              code: 'yz-btn(outlined size="xl" icon="$yzi-check-circle")',
              hint: 'Extra large outlined with icon'
            },
            // Danger outlined buttons with different sizes
            {
              props: {
                color: 'danger',
                outlined: true,
                size: 'sm',
                icon: '$yzi-trash-04'
              },
              code: 'yz-btn(color="danger" outlined size="sm" icon="$yzi-trash-04")',
              hint: 'Small danger outlined'
            },
            {
              props: {
                color: 'danger',
                outlined: true,
                size: 'md',
                icon: '$yzi-delete'
              },
              code: 'yz-btn(color="danger" outlined size="md" icon="$yzi-delete")',
              hint: 'Medium danger outlined'
            },
            {
              props: {
                color: 'danger',
                outlined: true,
                size: 'lg',
                icon: '$yzi-alert-triangle'
              },
              code: 'yz-btn(color="danger" outlined size="lg" icon="$yzi-alert-triangle")',
              hint: 'Large danger outlined'
            },
            {
              props: {
                color: 'danger',
                outlined: true,
                size: 'xl',
                icon: '$yzi-x-circle'
              },
              code: 'yz-btn(color="danger" outlined size="xl" icon="$yzi-x-circle")',
              hint: 'Extra large danger outlined'
            },
            // Loading states
            {
              props: {
                outlined: true,
                size: 'sm',
                loading: true,
                icon: '$yzi-refresh-cw-02'
              },
              code: 'yz-btn(outlined size="sm" loading icon="$yzi-refresh-cw-02")',
              hint: 'Small loading'
            },
            {
              props: {
                outlined: true,
                size: 'md',
                loading: true,
                icon: '$yzi-refresh-cw-02'
              },
              code: 'yz-btn(outlined size="md" loading icon="$yzi-refresh-cw-02")',
              hint: 'Medium loading'
            },
            {
              props: {
                outlined: true,
                size: 'lg',
                loading: true,
                icon: '$yzi-refresh-cw-02'
              },
              code: 'yz-btn(outlined size="lg" loading icon="$yzi-refresh-cw-02")',
              hint: 'Large loading'
            },
            {
              props: {
                outlined: true,
                size: 'xl',
                loading: true,
                icon: '$yzi-refresh-cw-02'
              },
              code: 'yz-btn(outlined size="xl" loading icon="$yzi-refresh-cw-02")',
              hint: 'Extra large loading'
            },
            // Disabled states
            {
              props: {
                outlined: true,
                size: 'sm',
                disabled: true,
                icon: '$yzi-lock-03'
              },
              code: 'yz-btn(outlined size="sm" disabled icon="$yzi-lock-03")',
              hint: 'Small disabled'
            },
            {
              props: {
                outlined: true,
                size: 'md',
                disabled: true,
                icon: '$yzi-lock-03'
              },
              code: 'yz-btn(outlined size="md" disabled icon="$yzi-lock-03")',
              hint: 'Medium disabled'
            },
            {
              props: {
                outlined: true,
                size: 'lg',
                disabled: true,
                icon: '$yzi-lock-03'
              },
              code: 'yz-btn(outlined size="lg" disabled icon="$yzi-lock-03")',
              hint: 'Large disabled'
            },
            {
              props: {
                outlined: true,
                size: 'xl',
                disabled: true,
                icon: '$yzi-lock-03'
              },
              code: 'yz-btn(outlined size="xl" disabled icon="$yzi-lock-03")',
              hint: 'Extra large disabled'
            }
          ]
        },
        {
          name: 'Icon Color Examples',
          id: 'icon-color-examples',
          description: 'Buttons with custom icon colors using iconColor prop',
          isGroup: true,
          buttons: [
            // Hex color examples
            {
              label: 'Hex Blue Icon',
              props: {
                prependIcon: 'mdi-star',
                iconColor: '#3B82F6'
              },
              code: 'yz-btn(label="Hex Blue Icon" prepend-icon="mdi-star" icon-color="#3B82F6")',
              hint: 'Hex color'
            },
            {
              label: 'Hex Red Icon',
              props: {
                appendIcon: 'mdi-heart',
                iconColor: '#EF4444'
              },
              code: 'yz-btn(label="Hex Red Icon" append-icon="mdi-heart" icon-color="#EF4444")',
              hint: 'Hex color'
            },
            {
              label: 'Hex Green Icon',
              props: {
                outlined: true,
                prependIcon: 'mdi-check',
                iconColor: '#10B981'
              },
              code: 'yz-btn(label="Hex Green Icon" outlined prepend-icon="mdi-check" icon-color="#10B981")',
              hint: 'Outlined with hex'
            },
            // YZ variable examples
            {
              label: 'YZ Primary Icon',
              props: {
                secondary: true,
                prependIcon: 'mdi-cog',
                iconColor: 'yz-primary-500'
              },
              code: 'yz-btn(label="YZ Primary Icon" secondary prepend-icon="mdi-cog" icon-color="yz-primary-500")',
              hint: 'Full YZ variable'
            },
            {
              label: 'YZ Danger Icon',
              props: {
                text: true,
                prependIcon: 'mdi-alert',
                iconColor: 'yz-danger-500'
              },
              code: 'yz-btn(label="YZ Danger Icon" text prepend-icon="mdi-alert" icon-color="yz-danger-500")',
              hint: 'Full YZ variable'
            },
            // Shorthand examples
            {
              label: 'White Icon',
              props: {
                color: '#1F2937',
                prependIcon: 'mdi-download',
                iconColor: 'white'
              },
              code: 'yz-btn(label="White Icon" color="#1F2937" prepend-icon="mdi-download" icon-color="white")',
              hint: 'Shorthand: var(--yz-white)'
            },
            {
              label: 'Success Icon',
              props: {
                outlined: true,
                prependIcon: 'mdi-check-circle',
                iconColor: 'success-500'
              },
              code: 'yz-btn(label="Success Icon" outlined prepend-icon="mdi-check-circle" icon-color="success-500")',
              hint: 'Shorthand: var(--yz-success-500)'
            },
            // Icon-only buttons with custom colors
            {
              label: 'Icon Only Hex',
              props: {
                iconOnly: true,
                icon: 'mdi-star',
                iconColor: '#8B5CF6'
              },
              code: 'yz-btn(icon-only icon="mdi-star" icon-color="#8B5CF6")',
              hint: 'Icon-only with hex'
            },
            {
              label: 'Icon Only YZ',
              props: {
                iconOnly: true,
                outlined: true,
                icon: 'mdi-heart',
                iconColor: 'yz-red-500'
              },
              code: 'yz-btn(icon-only outlined icon="mdi-heart" icon-color="yz-red-500")',
              hint: 'Icon-only with YZ variable'
            },
            {
              label: 'Icon Only Shorthand',
              props: {
                iconOnly: true,
                secondary: true,
                icon: 'mdi-bell',
                iconColor: 'warning-500'
              },
              code: 'yz-btn(icon-only secondary icon="mdi-bell" icon-color="warning-500")',
              hint: 'Icon-only with shorthand'
            },
            // Mixed with $yzi-icons
            {
              label: 'YZI Icon Hex',
              props: {
                prependIcon: '$yzi-star-01',
                iconColor: '#F59E0B'
              },
              code: 'yz-btn(label="YZI Icon Hex" prepend-icon="$yzi-star-01" icon-color="#F59E0B")',
              hint: '$yzi-icon with hex color'
            },
            {
              label: 'YZI Icon YZ Var',
              props: {
                outlined: true,
                icon: '$yzi-user-01',
                iconColor: 'yz-blue-500'
              },
              code: 'yz-btn(outlined icon="$yzi-user-01" icon-color="yz-blue-500")',
              hint: '$yzi-icon with YZ variable'
            }
          ]
        },
        {
          name: 'Button Colors',
          id: 'button-colors',
          description: 'Different button colors available',
          isGroup: true,
          buttons: [
            // Danger color variations
            {
              label: 'Danger',
              props: {
                color: 'danger'
              },
              code: 'yz-btn(label="Danger" color="danger")'
            },
            {
              label: 'Danger Secondary',
              props: {
                color: 'danger',
                secondary: true
              },
              code: 'yz-btn(label="Danger Secondary" color="danger" secondary)'
            },
            {
              label: 'Danger Outlined',
              props: {
                color: 'danger',
                outlined: true
              },
              code: 'yz-btn(label="Danger Outlined" color="danger" outlined)'
            },
            {
              label: 'Danger Text',
              props: {
                color: 'danger',
                text: true
              },
              code: 'yz-btn(label="Danger Text" color="danger" text)'
            },
            {
              label: 'Danger Underline',
              props: {
                color: 'danger',
                text: true,
                underline: true
              },
              code: 'yz-btn(label="Danger Underline" color="danger" text underline)'
            },
            {
              label: 'Danger Disabled',
              props: {
                color: 'danger',
                disabled: true
              },
              code: 'yz-btn(label="Danger Disabled" color="danger" disabled)'
            },
            {
              label: 'Danger Loading',
              props: {
                color: 'danger',
                loading: true
              },
              code: 'yz-btn(label="Danger Loading" color="danger" loading)'
            },
            {
              label: 'Danger with Prepend',
              props: {
                color: 'danger',
                prependIcon: 'mdi-delete'
              },
              code: 'yz-btn(label="Danger with Prepend" color="danger" prepend-icon="mdi-delete")'
            },
            {
              label: 'Danger with Append',
              props: {
                color: 'danger',
                appendIcon: 'mdi-trash-can'
              },
              code: 'yz-btn(label="Danger with Append" color="danger" append-icon="mdi-trash-can")'
            },
            {
              label: 'Danger Secondary Icon',
              props: {
                color: 'danger',
                secondary: true,
                prependIcon: 'mdi-close'
              },
              code: 'yz-btn(label="Danger Secondary Icon" color="danger" secondary prepend-icon="mdi-close")'
            },
            {
              label: 'Danger Outlined Icon',
              props: {
                color: 'danger',
                outlined: true,
                prependIcon: 'mdi-alert'
              },
              code: 'yz-btn(label="Danger Outlined Icon" color="danger" outlined prepend-icon="mdi-alert")'
            },
            {
              label: 'Danger Text Icon',
              props: {
                color: 'danger',
                text: true,
                prependIcon: 'mdi-alert'
              },
              code: 'yz-btn(label="Danger Text Icon" color="danger" text prepend-icon="mdi-alert")'
            },
            {
              label: 'Danger Underline Icon',
              props: {
                color: 'danger',
                text: true,
                underline: true,
                prependIcon: 'mdi-minus-circle'
              },
              code: 'yz-btn(label="Danger Underline Icon" color="danger" text underline prepend-icon="mdi-minus-circle")'
            }
          ]
        },
        {
          name: 'Custom Colors',
          id: 'custom-colors',
          description: 'Buttons with custom hex colors or YZ color classes',
          isGroup: true,
          buttons: [
            // Hex color examples
            {
              label: 'Custom Blue',
              props: {
                color: '#3B82F6'
              },
              code: 'yz-btn(label="Custom Blue" color="#3B82F6")',
              hint: 'Hex color'
            },
            {
              label: 'Custom Green',
              props: {
                color: '#10B981'
              },
              code: 'yz-btn(label="Custom Green" color="#10B981")',
              hint: 'Hex color'
            },
            {
              label: 'Custom Purple',
              props: {
                color: '#8B5CF6'
              },
              code: 'yz-btn(label="Custom Purple" color="#8B5CF6")',
              hint: 'Hex color'
            },
            {
              label: 'Custom Orange',
              props: {
                color: '#F59E0B'
              },
              code: 'yz-btn(label="Custom Orange" color="#F59E0B")',
              hint: 'Hex color'
            },
            // Design system color class examples
            {
              label: 'Success Button',
              props: {
                color: 'success-500'
              },
              code: 'yz-btn(label="Success Button" color="success-500")',
              hint: 'CSS class: bg-success-500'
            },
            {
              label: 'Warning Button',
              props: {
                color: 'warning-500'
              },
              code: 'yz-btn(label="Warning Button" color="warning-500")',
              hint: 'CSS class: bg-warning-500'
            },
            {
              label: 'Neutral Button',
              props: {
                color: 'neutral-600'
              },
              code: 'yz-btn(label="Neutral Button" color="neutral-600")',
              hint: 'CSS class: bg-neutral-600'
            },
            {
              label: 'Blue Button',
              props: {
                color: 'blue-500'
              },
              code: 'yz-btn(label="Blue Button" color="blue-500")',
              hint: 'CSS class: bg-blue-500'
            },
            // Custom colors with different variants
            {
              label: 'Custom Outlined',
              props: {
                color: '#EC4899',
                outlined: true
              },
              code: 'yz-btn(label="Custom Outlined" color="#EC4899" outlined)',
              hint: 'Hex with outlined variant'
            },
            {
              label: 'Custom Text',
              props: {
                color: '#06B6D4',
                text: true
              },
              code: 'yz-btn(label="Custom Text" color="#06B6D4" text)',
              hint: 'Hex with text variant'
            },
            {
              label: 'Custom Secondary',
              props: {
                color: '#EF4444',
                secondary: true
              },
              code: 'yz-btn(label="Custom Secondary" color="#EF4444" secondary)',
              hint: 'Hex with secondary variant'
            },
            // Custom colors with icons
            {
              label: 'Custom with Icon',
              props: {
                color: '#8B5CF6',
                prependIcon: 'mdi-star'
              },
              code: 'yz-btn(label="Custom with Icon" color="#8B5CF6" prepend-icon="mdi-star")',
              hint: 'Hex color with icon'
            },
            // Custom text colors with hex
            {
              label: 'Custom Text Color',
              props: {
                color: '#F3F4F6',
                textColor: '#1F2937'
              },
              code: 'yz-btn(label="Custom Text Color" color="#F3F4F6" text-color="#1F2937")',
              hint: 'Hex background and text color'
            },
            {
              label: 'White with Red Text',
              props: {
                color: '#FFFFFF',
                textColor: '#DC2626'
              },
              code: 'yz-btn(label="White with Red Text" color="#FFFFFF" text-color="#DC2626")',
              hint: 'White background, red text'
            },
            // Custom text colors with YZ variables
            {
              label: 'YZ Variable Text',
              props: {
                color: '#1F2937',
                textColor: 'yz-primary-300'
              },
              code: 'yz-btn(label="YZ Variable Text" color="#1F2937" text-color="yz-primary-300")',
              hint: 'Full YZ variable name'
            },
            {
              label: 'Shorthand Variable',
              props: {
                color: '#EF4444',
                textColor: 'white'
              },
              code: 'yz-btn(label="Shorthand Variable" color="#EF4444" text-color="white")',
              hint: 'Shorthand: var(--yz-white)'
            }
          ]
        },
        {
          name: 'Block Button',
          id: 'block-button',
          description: 'Full-width button',
          label: 'Block',
          props: {
            block: true
          },
          code: 'yz-btn(label="Block" block)'
        }
      ],
      buttonProps: [
        {
          name: 'label',
          type: 'String',
          default: 'null',
          description: 'Button text content'
        },
        {
          name: 'color',
          type: 'String',
          default: 'primary',
          description: 'Button color scheme. Accepts "primary", "danger", hex colors (e.g. "#3B82F6"), or YZ design system color classes (e.g. "success-500", "warning-500", "neutral-600") which become "bg-{color}" CSS classes. See Colors component for available design system colors.'
        },
        {
          name: 'textColor',
          type: 'String',
          default: 'null',
          description: 'Button text color. Accepts hex colors (e.g. "#FFFFFF"), full YZ variable names (e.g. "yz-white"), or shorthand names (e.g. "white" becomes "var(--yz-white)")'
        },
        {
          name: 'size',
          type: 'String',
          default: 'md',
          description: 'Button size (sm, md, lg, xl)'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Shows loading spinner'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disables button interactions'
        },
        {
          name: 'outlined',
          type: 'Boolean',
          default: 'false',
          description: 'Outlined button style'
        },
        {
          name: 'block',
          type: 'Boolean',
          default: 'false',
          description: 'Full-width button'
        },
        {
          name: 'text',
          type: 'Boolean',
          default: 'false',
          description: 'Text-only button without background'
        },
        {
          name: 'underline',
          type: 'Boolean',
          default: 'false',
          description: 'Underlined text button'
        },
        {
          name: 'prependIcon',
          type: 'String',
          default: 'null',
          description: 'Icon before text'
        },
        {
          name: 'appendIcon',
          type: 'String',
          default: 'null',
          description: 'Icon after text'
        },
        {
          name: 'icon',
          type: 'String',
          default: 'null',
          description: 'Single icon button (no label). Example icon="$yzi-icon-name". Automatically sizes button and removes min-width.'
        },
        {
          name: 'iconColor',
          type: 'String',
          default: 'null',
          description: 'Icon color. Accepts hex colors (e.g. "#3B82F6"), full YZ variable names (e.g. "yz-primary-500"), or shorthand names (e.g. "white" becomes "var(--yz-white)")'
        },
        {
          name: 'height',
          type: 'String',
          default: 'null',
          description: 'Custom button height in pixels'
        },
        {
          name: 'width',
          type: 'String',
          default: 'null',
          description: 'Custom button width in pixels'
        },
        {
          name: 'iconOnly',
          type: 'Boolean',
          default: 'false',
          description: 'Icon-only button (circular shape)'
        },
        {
          name: 'secondary',
          type: 'Boolean',
          default: 'false',
          description: 'Secondary button variant with neutral styling'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>